import { centerOfMass } from '@turf/turf'
import {
  ExtraLabelDisplayFn,
  UnitDisplayPropertiesFn,
} from 'features/siteMapViz'
import { isNil } from 'lodash'
import {
  MapFeatureType,
  useFeatureInteractionContext,
} from 'pages/siteView/siteMap/contexts/FeatureInteractionContext'
import { MapInteractionAPIs } from 'pages/siteView/siteMap/SiteViewMap'
import { FC, useMemo } from 'react'
import { GeckoGeographicPin, GeckoUnit } from 'types'

import { MapboxGeographicPinFeatureFactory } from './MapboxGeographicPinFeature'
import { MapboxUnitFeature } from './MapboxUnitFeature'

interface MapboxFeatureDispatcherProps {
  units: ReadonlyArray<GeckoUnit>
  getUnitDisplayProperties: UnitDisplayPropertiesFn
  shouldDisplayTaggedUnit: NonNullable<
    MapInteractionAPIs['shouldDisplayTaggedUnit']
  >
  getExtraLabelDisplay: ExtraLabelDisplayFn
  pins: readonly GeckoGeographicPin[]
}
export const MapboxFeatureDispatcher: FC<MapboxFeatureDispatcherProps> = (
  props
) => {
  const {
    units,
    getUnitDisplayProperties,
    shouldDisplayTaggedUnit,
    getExtraLabelDisplay,
  } = props
  const { onFeatureClick } = useFeatureInteractionContext()
  const allUnitMarkers = useMemo(
    () =>
      units
        .map((u) => {
          if (isNil(u.data.geography) || !shouldDisplayTaggedUnit(u.id)) {
            return null
          }

          const unitDisplayProperties = getUnitDisplayProperties(u.id)

          const [longitude, latitude] = centerOfMass(u.data.geography).geometry
            .coordinates

          return (
            <MapboxUnitFeature
              key={u.id}
              id={u.id}
              name={u.data.name}
              longitude={longitude}
              latitude={latitude}
              extraLabelDisplay={getExtraLabelDisplay(u.id)}
              color={unitDisplayProperties.color}
              pattern={unitDisplayProperties.pattern}
              onClick={(e) =>
                onFeatureClick(e, {
                  type: MapFeatureType.Unit,
                  unitID: u.id,
                })
              }
            />
          )
        })
        .filter((v) => !isNil(v)),
    [
      units,
      shouldDisplayTaggedUnit,
      getUnitDisplayProperties,
      getExtraLabelDisplay,
      onFeatureClick,
    ]
  )

  const allPinMarkers = useMemo(
    () =>
      props.pins.map((pin) => (
        <MapboxGeographicPinFeatureFactory key={pin.id} pin={pin} />
      )),
    [props.pins]
  )

  return [...allUnitMarkers, ...allPinMarkers]
}
